import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Card,
  TextField,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  Checkbox,
  Divider,
  FormHelperText
} from '@material-ui/core';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import Autocomplete from '@material-ui/lab/Autocomplete';


const RequestPromotionFixedPrice = ({
  values,
  setFieldValue,
  touched,
  errors
}) => {
  const { storeActiveCategoriesWithAllActiveParents, loadingCategories } = useSelector((state: IReduxState) => state.categories);
  const { store_products: { catalogActiveProducts }, productsLoading } = useSelector((state: IReduxState) => state.products);


  const renderOption = (option, { inputValue, type }) => {
    const optionText = type === 'categories' ? option?.nameWithPath : option.name;
    const matches = match(optionText, inputValue);
    const parts = parse(optionText, matches);

    return (
      <div>
        {parts.map((part, index) => (
          <span key={index} className={part.highlight ? 'text-primary font-bold' : ''}>
            {part.text}
          </span>
        ))}
        {type === 'listings' && Boolean(option.price) && (
          <span className="ml-2 text-green">${option.price}</span>
        )}
      </div>
    );
  };

  return (
    <Card className="p-5 mt-4" elevation={0}>
      <Typography variant="h5" className="font-bold mb-5">
        Applies To <span className="text-error">*</span>
      </Typography>
      <FormControl component="fieldset" className="w-full">
        <RadioGroup
          aria-label="applies to"
          name="fixedPrice.appliesTo"
          value={values.fixedPrice.appliesTo.name}
          onChange={(event) => {
            setFieldValue('fixedPrice.appliesTo.name', event.target.value);
          }}
        >
          <FormControlLabel
            value="Storewide"
            control={<Radio size="small" color="primary" />}
            label="Storewide"
          />
          <FormControlLabel
            value="All Products"
            control={<Radio size="small" color="primary" />}
            label="All Products"
          />
          <FormControlLabel
            value="All Treatments"
            control={<Radio size="small" color="primary" />}
            label="All Treatments"
          />
          <FormControlLabel
            value="Specific Categories/Listings"
            control={
              <Radio
                size="small"
                color="primary"
              />
            }
            label="Specific Categories/Listings"
          />
          {values.fixedPrice.appliesTo.name === 'Specific Categories/Listings' &&
          <div className="ml-7 flex-column">
            <FormControlLabel
              name="Categories"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.fixedPrice.appliesTo.isCategories}
                  onChange={(e) => setFieldValue('fixedPrice.appliesTo.isCategories', e.target.checked)}
                />
              }
              label="Categories"
            />
            {values.fixedPrice.appliesTo.isCategories &&
            <>
              {loadingCategories ?
                <div className="my-5">
                  <span className="text-muted">Preparing Categories Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  size="small"
                  className="my-5"
                  value={values.fixedPrice.appliesTo.value.categories}
                  options={storeActiveCategoriesWithAllActiveParents}
                  getOptionLabel={(option) => option.nameWithPath}
                  onChange={(e, value) => {
                    setFieldValue(
                      'fixedPrice.appliesTo.value.categories',
                      value !== null ? value : values.fixedPrice.appliesTo.value.categories
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="fixedPrice.appliesTo.value.categories"
                      variant="outlined"
                      label="Categories"
                      required
                      error={touched.fixedPrice?.appliesTo?.value?.categories && Boolean(errors.fixedPrice?.appliesTo?.value?.categories)}
                      helperText={touched.fixedPrice?.appliesTo?.value?.categories && errors.fixedPrice?.appliesTo?.value?.categories}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'categories' })}
                />
              }
            </>
            }
            <FormControlLabel
              name="Listings"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.fixedPrice.appliesTo.isListings}
                  onChange={(e) => setFieldValue('fixedPrice.appliesTo.isListings', e.target.checked)}
                />
              }
              label="Listings"
            />
            {values.fixedPrice.appliesTo.isListings &&
            <>
              {productsLoading ?
                <div className="my-5">
                  <span className="text-muted">Preparing Listings Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  noOptionsText={
                    <Link
                      to="/request-sku" target="_blank" onMouseDown={() => {
                        window.open('/request-sku', '_blank');
                      }}
                    >
                      Request Listings
                    </Link>
                  }
                  size="small"
                  className="my-5"
                  value={values.fixedPrice.appliesTo.value.listings}
                  options={catalogActiveProducts}
                  getOptionLabel={(option) => option.name}
                  onChange={(e, value) => {
                    setFieldValue(
                      'fixedPrice.appliesTo.value.listings',
                      value !== null ? value : values.fixedPrice.appliesTo.value.listings
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="fixedPrice.appliesTo.value.listings"
                      variant="outlined"
                      label="Listings"
                      required
                      error={touched.fixedPrice?.appliesTo?.value?.listings && Boolean(errors.fixedPrice?.appliesTo?.value?.listings)}
                      helperText={touched.fixedPrice?.appliesTo?.value?.listings && errors.fixedPrice?.appliesTo?.value?.listings}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'listings' })}
                />
              }
            </>
            }
          </div>
          }
          {(touched.fixedPrice?.appliesTo?.isCategories ||
                        touched.fixedPrice?.appliesTo?.isListings) &&
                        (Boolean(errors.fixedPrice?.appliesTo?.isCategories) ||
                        Boolean(errors.fixedPrice?.appliesTo?.isListings)) &&
                        <FormHelperText>
                          <Typography color="error" variant="caption">
                            At least one needs to be selected
                          </Typography>
                        </FormHelperText>
          }
          {touched.fixedPrice?.appliesTo?.name && Boolean(errors.fixedPrice?.appliesTo?.name) &&
          <FormHelperText>
            <Typography color="error" variant="caption">
              {errors.fixedPrice.appliesTo.name}
            </Typography>
          </FormHelperText>
          }
        </RadioGroup>
      </FormControl>
      <Divider className="my-5" />
      <Typography variant="h5" className="font-bold mb-5">
        Does Not Apply To
      </Typography>
      <FormControl component="fieldset" className="w-full">
        <RadioGroup
          aria-label="not applies to"
          name="fixedPrice.notAppliesTo"
          value={values.fixedPrice.notAppliesTo.name}
          onChange={(event) => {
            setFieldValue('fixedPrice.notAppliesTo.name', event.target.value);
          }}
        >
          <FormControlLabel
            value="None"
            control={<Radio size="small" color="primary" />}
            label="None"
          />
          <FormControlLabel
            value="All Products"
            control={<Radio size="small" color="primary" />}
            label="All Products"
          />
          <FormControlLabel
            value="All Treatments"
            control={<Radio size="small" color="primary" />}
            label="All Treatments"
          />
          <FormControlLabel
            value="Specific Categories/Listings"
            control={
              <Radio
                size="small"
                color="primary"
              />
            }
            label="Specific Categories/Listings"
          />
          {values.fixedPrice.notAppliesTo.name === 'Specific Categories/Listings' &&
          <div className="ml-7 flex-column">
            <FormControlLabel
              name="Categories"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.fixedPrice.notAppliesTo.isCategories}
                  onChange={(e) => setFieldValue('fixedPrice.notAppliesTo.isCategories', e.target.checked)}
                />
              }
              label="Categories"
            />
            {values.fixedPrice.notAppliesTo.isCategories &&
            <>
              {loadingCategories ?
                <div className="my-5">
                  <span className="text-muted">Preparing Categories Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  size="small"
                  className="my-5"
                  value={values.fixedPrice.notAppliesTo.value.categories}
                  options={storeActiveCategoriesWithAllActiveParents}
                  getOptionLabel={(option) => option.nameWithPath}
                  onChange={(e, value) => {
                    setFieldValue(
                      'fixedPrice.notAppliesTo.value.categories',
                      value !== null ? value : values.fixedPrice.notAppliesTo.value.categories
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="fixedPrice.notAppliesTo.value.categories"
                      variant="outlined"
                      label="Categories"
                      required
                      error={touched.fixedPrice?.notAppliesTo?.value?.categories && Boolean(errors.fixedPrice?.notAppliesTo?.value?.categories)}
                      helperText={touched.fixedPrice?.notAppliesTo?.value?.categories && errors.fixedPrice?.notAppliesTo?.value?.categories}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'categories' })}
                />
              }
            </>
            }
            <FormControlLabel
              name="Listings"
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  checked={values.fixedPrice.notAppliesTo.isListings}
                  onChange={(e) => setFieldValue('fixedPrice.notAppliesTo.isListings', e.target.checked)}
                />
              }
              label="Listings"
            />
            {values.fixedPrice.notAppliesTo.isListings &&
            <>
              {productsLoading ?
                <div className="my-5">
                  <span className="text-muted">Preparing Listings Data...</span>
                  <LinearProgress />
                </div>
                :
                <Autocomplete
                  multiple
                  fullWidth
                  disableClearable
                  noOptionsText={
                    <Link
                      to="/request-sku" target="_blank" onMouseDown={() => {
                        window.open('/request-sku', '_blank');
                      }}
                    >
                      Request Listings
                    </Link>
                  }
                  size="small"
                  className="my-5"
                  value={values.fixedPrice.notAppliesTo.value.listings}
                  options={catalogActiveProducts}
                  getOptionLabel={(option) => option.name}
                  onChange={(e, value) => {
                    setFieldValue(
                      'fixedPrice.notAppliesTo.value.listings',
                      value !== null ? value : values.fixedPrice.notAppliesTo.value.listings
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="fixedPrice.notAppliesTo.value.listings"
                      variant="outlined"
                      label="Listings"
                      required
                      error={touched.fixedPrice?.notAppliesTo?.value?.listings && Boolean(errors.fixedPrice?.notAppliesTo?.value?.listings)}
                      helperText={touched.fixedPrice?.notAppliesTo?.value?.listings && errors.fixedPrice?.notAppliesTo?.value?.listings}
                    />
                  )}
                  renderOption={(option, { inputValue }) => renderOption(option, { inputValue, type: 'listings' })}
                />
              }
            </>
            }
          </div>
          }
          {(touched.fixedPrice?.notAppliesTo?.isCategories ||
                        touched.fixedPrice?.notAppliesTo?.isListings) &&
                        (Boolean(errors.fixedPrice?.notAppliesTo?.isCategories) ||
                        Boolean(errors.fixedPrice?.notAppliesTo?.isListings)) &&
                        values.fixedPrice.notAppliesTo.name === 'Specific Categories/Listings' &&
                        <FormHelperText>
                          <Typography color="error" variant="caption">
                            At least one needs to be selected
                          </Typography>
                        </FormHelperText>
          }
          {touched.fixedPrice?.notAppliesTo?.name && Boolean(errors.fixedPrice?.notAppliesTo?.name) &&
          <FormHelperText>
            <Typography color="error" variant="caption">
              {errors.fixedPrice.notAppliesTo.name}
            </Typography>
          </FormHelperText>
          }
        </RadioGroup>
      </FormControl>
    </Card>
  );
};

export default RequestPromotionFixedPrice;
